import { Curve, VictoryLine } from "victory";
import { DateTime } from "luxon";
import { Point, PointHistoryItem, PointType } from "~point/point.model";
import { roundToDecimal } from "~utils/value";
import { ChartMeta, Dataset } from "./chart.types";
import { drawColors } from "./chart.theme";
import { getPointValueLabel } from "../point/point.utils";
import { PointsHistoryResult } from "../point/point-history/point-history.queries";
import { parsePointName } from "../graphics/graphics.utils";

export function getYDomainPadded(
  domain: [number, number],
  factor: number,
): [number, number] {
  const [yMin, yMax] = domain;
  const yRange = yMax - yMin;
  const paddedYMin = yMin - yRange * factor;
  const paddedYMax = yMax + yRange * factor;

  return [roundToDecimal(paddedYMin, 2), roundToDecimal(paddedYMax, 2)];
}

export function getXDomainClamped(
  x: [DateTime, DateTime],
  maxRange: number,
): [DateTime, DateTime] {
  const zoomStart = x[0].toMillis();
  const zoomEnd = x[1].toMillis();
  const range = zoomEnd - zoomStart;

  if (range > maxRange) {
    return [
      DateTime.fromMillis(zoomEnd - maxRange),
      DateTime.fromMillis(zoomEnd),
    ];
  }

  return x;
}

export function calcYDomain(values: PointHistoryItem[]): [number, number] {
  const yValues = values
    .map((d) => getGraphValue(d))
    .filter((v): v is number => v !== null);

  const yMax = Math.max(...yValues);
  const yMin = Math.min(...yValues);
  // Add a little bit of padding to the domain in case the chart lines are straight (min and max values are the same)
  return yMin === yMax ? [yMin - 1, yMax + 1] : [yMin, yMax];
}

export function calcChartMeta(values: PointHistoryItem[]): ChartMeta {
  const yValues = values
    .map((d) => getGraphValue(d))
    .filter((v): v is number => v !== null);
  const xValues = values.map((d) => d.timestamp.toMillis());

  const yMax = Math.max(...yValues);
  const yMin = Math.min(...yValues);
  const yDomain = [yMin, yMax] as [number, number];

  const xMax = Math.max(...xValues);
  const xMin = Math.min(...xValues);
  const xDomain = [DateTime.fromMillis(xMin), DateTime.fromMillis(xMax)] as [
    DateTime,
    DateTime,
  ];

  return { yDomain, xDomain, total: values.length };
}

export function renderCompareLine({
  x,
  yMax,
  yMin,
}: {
  x: DateTime;
  yMin: number;
  yMax: number;
}) {
  return (
    <VictoryLine
      data={[
        { x: x.toJSDate(), y: yMin },
        { x: x.toJSDate(), y: yMax },
      ]}
      style={{
        data: {
          stroke: "#000",
          strokeWidth: 2,
          strokeDasharray: "4",
          pointerEvents: "none",
        },
      }}
    />
  );
}

export function renderChartLine({
  kind,
  dataset,
  index,
  zoomXDomain,
  color,
}: {
  kind: "main" | "brush";
  dataset: Dataset;
  index: number;
  zoomXDomain?: [DateTime, DateTime];
  color?: string;
}) {
  const pointType = dataset.point.type;
  const interpolation =
    pointType === PointType.AI ||
    pointType === PointType.AO ||
    pointType === PointType.Control
      ? "linear"
      : "step";

  let data = dataset.values.map((d) => {
    // Convert DateTime to Date for Victory charts
    const timestamp = d.timestamp.toJSDate();
    return {
      ...d,
      timestamp,
      // Add color to the datum so that we have access to it eg. in tooltip
      color: color || dataset.color,
      value: getGraphValue(d),
    };
  });

  // Filter data based on the zoom domain
  if (zoomXDomain) {
    const zoomStart = zoomXDomain[0].toMillis();
    const zoomEnd = zoomXDomain[1].toMillis();

    data = data.filter((d) => {
      const x = d.timestamp.getTime();
      return x >= zoomStart && x <= zoomEnd;
    });

    // Filter out data points whose value has not changed
    data = data.filter((d, i) => {
      if (
        i === 0 ||
        i === data.length - 1 ||
        d.value !== data[i - 1].value ||
        d.value !== data[i + 1].value
      ) {
        return true;
      }
      return false;
    });
  }

  /**
   * NOTE: we need to provide a custom data component for the main chart
   * in order to have a `className` on the rendered `path` element so that
   * we are able to calculate the active path point on hover.
   * See `chart-cursor.tsx` component for more details.
   */
  const dataComponent =
    kind === "main" ? <Curve className="chart-line" /> : undefined;

  return (
    <VictoryLine
      key={`line-${index}-${dataset.point.id}`}
      name={`line-${index}`}
      interpolation={interpolation}
      data={data}
      dataComponent={dataComponent}
      x="timestamp"
      y="value"
      style={{
        data: {
          stroke: color || dataset.color,
          pointerEvents: "none",
        },
      }}
    />
  );
}

export function formatTooltipValue(item: PointHistoryItem) {
  if (item.value === null) return "N/A";
  return getPointValueLabel({
    type: item.type,
    unit: item.unit,
    value: {
      value: getGraphValue(item),
      label: item.tooltipLabel,
    },
  });
}

export function formatChartValue({
  unit,
  value,
}: {
  unit: PointHistoryItem["unit"];
  value: PointHistoryItem["value"];
}) {
  if (value === null) return "N/A";
  const unitPart = unit ? ` ${unit}` : "";
  const valuePart = roundToDecimal(value, 2);
  return `${valuePart}${unitPart}`;
}

const dayMs = 24 * 60 * 60 * 1000;

export function formatXTick({
  x,
  xMin,
  xMax,
}: {
  x: DateTime;
  xMin: DateTime;
  xMax: DateTime;
}) {
  // Show hours if the range is less than 1 day
  if (xMax.toMillis() - xMin.toMillis() < dayMs) {
    return x.toLocaleString({
      hour: "numeric",
      minute: "numeric",
    });
  }

  // Otherwise show month, day and time
  return x.toLocaleString({
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
  });
}

export function convertValueFromOldRangeToNewRange(
  value: number,
  oldRange: [number, number],
  newRange: [number, number],
): number {
  const oRange = oldRange[1] - oldRange[0];
  const nRange = newRange[1] - newRange[0];
  return ((value - oldRange[0]) * nRange) / (oRange || 1) + newRange[0];
}

type ConversionToTimeStampAndValue = {
  x: number;
  y: number;
  oldRangeX: [number, number];
  oldRangeY: [number, number];
  newRangeX: [DateTime, DateTime];
  newRangeY: [number, number];
};

type ConversionToCoordinates = {
  timestamp: DateTime;
  value: number;
  oldRangeX: [DateTime, DateTime];
  oldRangeY: [number, number];
  newRangeX: [number, number];
  newRangeY: [number, number];
};

export function timestampAndValueToCoordinates({
  timestamp,
  value,
  oldRangeX,
  oldRangeY,
  newRangeX,
  newRangeY,
}: ConversionToCoordinates) {
  const timestampMs = timestamp.toMillis();
  const oldRangeXStart = oldRangeX[0].toMillis();
  const oldRangeXEnd = oldRangeX[1].toMillis();
  const newX = convertValueFromOldRangeToNewRange(
    timestampMs,
    [oldRangeXStart, oldRangeXEnd],
    newRangeX,
  );
  const newY = convertValueFromOldRangeToNewRange(value, oldRangeY, newRangeY);
  return { x: newX, y: newY };
}

export function coordinatesToTimestampAndValue({
  x,
  y,
  newRangeX,
  newRangeY,
  oldRangeX,
  oldRangeY,
}: ConversionToTimeStampAndValue) {
  const newX = convertValueFromOldRangeToNewRange(x, oldRangeX, [
    newRangeX[0].toMillis(),
    newRangeX[1].toMillis(),
  ]);
  const newY = convertValueFromOldRangeToNewRange(y, oldRangeY, newRangeY);

  return { timeStamp: DateTime.fromMillis(newX), value: newY };
}

export type ActivePoint = PointHistoryItem & {
  originalValue?: number;
  color: string;
  name: string;
};

type ChartBinarySearch = { values: { timestamp: DateTime }[] };

export function findClosestPointByTimeStamp<T extends ChartBinarySearch>(
  dataSet: T,
  timestamp: DateTime,
): T["values"][number] | undefined {
  const point = findPointBinarySearch(dataSet.values, timestamp);

  if (!point) {
    return undefined;
  }

  return point;
}

const MAX_ITERATIONS = 500;
// Use binary search to optimize value lookup
function findPointBinarySearch<T extends { timestamp: DateTime }>(
  values: T[],
  timestamp: DateTime,
): T | undefined {
  let foundValue: T | undefined = undefined;
  let start = 0;
  let end = values.length - 1;
  let i = 0;

  while (i <= MAX_ITERATIONS) {
    i++;
    const pos = Math.floor((start + end) / 2);
    const curTimestamp = values[pos]?.timestamp;
    const nextTimestamp = values[pos + 1]?.timestamp;

    if (
      i >= MAX_ITERATIONS ||
      start >= end ||
      (curTimestamp <= timestamp && nextTimestamp >= timestamp)
    ) {
      foundValue = values[pos];
      break;
    }

    if (curTimestamp < timestamp) {
      start = pos + 1;
    } else {
      end = pos - 1;
    }
  }

  return foundValue;
}

// Based on https://stackoverflow.com/a/47935325
export function findPointAt(path: SVGPathElement, x: number): SVGPoint | null {
  const pathLength = path.getTotalLength();

  if (pathLength === 0) {
    return null;
  }

  let start = 0;
  let end = pathLength;
  let target = (start + end) / 2;

  /**
   * Guard against infinite loop which can happen if there are gaps in the path.
   * 100 iterations should be plenty enough to find the point with binary search.
   */
  const maxIterations = 100;
  let i = 0;

  // Walk along the path using binary search to locate the point with the x value
  while (target >= start && target <= pathLength && i < maxIterations) {
    const pos = path.getPointAtLength(target);

    // Use a threshold instead of strict equality to handle floating point precision
    if (Math.abs(pos.x - x) < 0.001) {
      return pos;
    } else if (pos.x > x) {
      end = target;
    } else {
      start = target;
    }

    target = (start + end) / 2;
    i++;
  }

  return null;
}

// Assign draw colors whenever the visible points in the chart changes
export function assignDrawColors(ids: string[]) {
  // First we remove all colors from the drawColors array that are not visible anymore
  drawColors.forEach((c) => (c.id = undefined));

  //if point is more than available colors let's push brand color in the arrray
  if (ids.length > drawColors.length) {
    while (ids.length !== drawColors.length) {
      drawColors.push({ color: "#003865" });
    }
  }
  ids
    .filter((c) => !drawColors.find((o) => o.id === c))
    .forEach((c) => (drawColors.find((o) => o.id === undefined)!.id = c));
}
export function clearDrawColor(id: string) {
  const colorObj = drawColors.find((c) => c.id === id);
  if (colorObj) {
    colorObj.id = undefined;
  }
}

export function getDrawColor(id: string): string {
  // Return the color this pointId is assigned to
  // Othewise return black (this should never be the case)
  return drawColors.find((c) => c.id === id)?.color || "#cacaca";
}
export function setNewDrawColor(id: string, newColor: string) {
  // Reset color
  drawColors.find((c) => {
    if (c.id === id) {
      c.color = newColor;
    }
  });
}

// Since value limits don't have unique ids we will have to construct one in the format of
// pointId:limitIndex
// This id is constructed because we need to assign a color for every point and limit in the chart
export function getLimitId(pointId: number, limitIndex: number): string {
  return `${pointId}:${limitIndex}`;
}
export type ChartDatasetWithColor = {
  point: Point;
  values: PointHistoryItem[];
  color: string;
};

export function getDatasets(
  chartData: PointsHistoryResult,
  allVisibleIds: string[],
): ChartDatasetWithColor[] {
  // First filter out actual points and map datasets for points
  const dataset = chartData
    .filter((d) => allVisibleIds.includes(String(d.point.id)))
    .map((d) => ({ ...d, color: getDrawColor(String(d.point.id)) }));

  // Find out any limit values we want to show
  const limitIds = allVisibleIds.map((limit) => {
    const { pointBaseName: pointId, limit: limitIdx } = parsePointName(limit);
    if (pointId && limitIdx) {
      return { pointId: Number(pointId), limitIdx: Number(limitIdx) };
    }
  });

  // Add limit values to the datasets
  chartData.forEach((data) => {
    const limitIdxs = limitIds
      .map((limit) => {
        if (limit?.pointId === data.point.id) {
          return limit.limitIdx;
        }
      })
      .filter((limit): limit is number => !!limit);

    limitIdxs.forEach((limit) => {
      dataset.push({
        color: getDrawColor(getLimitId(data.point.id, limit)),
        point: data.point,
        values: data.values.map((val) => ({
          ...val,
          value: val.limits ? val.limits[limit - 1].value : 0,
        })),
      });
    });
  });

  return dataset;
}

export const getGraphValue = (item: PointHistoryItem): number | null => {
  return item?.deviceState === 0 ? item.value : null;
};
